import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Users, ArrowLeft, ArrowUpCircle, CreditCard,
    Trash2, CheckSquare, Square, Search, Filter,
    UserPlus
} from 'lucide-react';
import DataTable1 from '../components/ui/DataTable1';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { useToast } from '../hooks/useToast';
import { client, fetchGroup, fetchGroupsUtil, fetchGroupsUtils, fetchPOSCustomers } from '../apiHelper';
import { X, FileBadge2Icon } from 'lucide-react';

const GroupMemberManager = () => {
    const location = useLocation();
    // const { groupId } = useParams();
    const { groupId, storeId } = useMemo(() => ({
        groupId: location.state?.groupId || null,
        storeId: location.state?.storeId || null,
    }), [location.state]);

    const navigate = useNavigate();
    const { showToast } = useToast();

    // --- State ---
    const [user, setUser] = useState(null);
    const [groupData, setGroupData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [groupOptions, setGroupOptions] = useState([]);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);

    // Modal States
    const [activeModal, setActiveModal] = useState(null); // 'promote' | 'credit'
    const [processing, setProcessing] = useState(false);

    // Form States for Modals
    const [targetGroupId, setTargetGroupId] = useState(null);
    const [creditLimitAmount, setCreditLimitAmount] = useState(0.00);
    const [targetCustomerId, setTargetCustomerId] = useState(null);

    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerError, setCustomerError] = useState(null);

    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user || !storeId) return;
        setLoadingCustomers(true);
        setCustomerError(null);
        try {
            const fetchedCustomers = await fetchPOSCustomers({ ...user, store_id: storeId }, searchQuery);
            // console.log(fetchedCustomers)
            setCustomerOptions(fetchedCustomers.map(customer => ({
                label: `${customer.user?.first_name || ''} ${customer.user?.middle_name || ''} ${customer.user?.last_name || ''}`,
                // label: `${customer.user?.first_name || ''} ${customer.user?.last_name || ''} (${customer.user?.credential?.logon_id || 'N/A'})`,
                value: customer.member_id,
                customerData: customer
            })));
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load customers: ${err.message || err}`;
            setCustomerError(errorMessage);
            setTimeout(() => showToast(errorMessage, 'error'), 0);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, storeId, showToast]);


    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchGroup(groupId);
            const groupUtilResponse = await fetchGroupsUtils();
            setGroupData(response)
            setGroupOptions(groupUtilResponse.map(g => ({
                label: `${g.name} (${g.member_count} ${g.member_count === 1 ? 'member' : 'members'})`,
                value: g.mbrgrp_id,
            })));
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to load group details", "error");
        } finally {
            setLoading(false);
        }
    }, [user, groupId, showToast]);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to use the Point of Sale.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    useEffect(() => {
        fetchData();
        fetchCustomers();
        setTargetGroupId(groupId);
    }, [fetchData]);

    // --- Selection Logic ---
    const toggleSelectAll = () => {
        if (selectedMemberIds.length === groupData?.members.length) {
            setSelectedMemberIds([]);
        } else {
            setSelectedMemberIds(groupData?.members.map(m => m.member_id) || []);
        }
    };

    const toggleSelectMember = (id) => {
        setSelectedMemberIds(prev =>
            prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
        );
    };

    // --- Columns Definition ---
    const columns = [
        {
            key: 'selection',
            label: (
                <button onClick={toggleSelectAll} className="text-purple-600">
                    {selectedMemberIds.length > 0 && selectedMemberIds.length === groupData?.members.length
                        ? <CheckSquare size={18} /> : <Square size={18} />}
                </button>
            ),
            render: (_, row) => (
                <button onClick={() => toggleSelectMember(row.member_id)} className="text-gray-400">
                    {selectedMemberIds.includes(row.member_id)
                        ? <CheckSquare size={18} className="text-purple-600" /> : <Square size={18} />}
                </button>
            )
        },
        {
            key: 'user',
            label: 'Member Details',
            render: (user) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center font-bold text-xs text-gray-600">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">
                            {user?.first_name} {user?.middle_name} {user?.last_name}
                        </div>
                        {/* Subtitle replaced with employeeid from business_profiles */}
                        <div className="text-xs text-gray-500 font-mono">
                            {user?.business_profiles?.[0]?.employeeid || 'No ID assigned'}
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: 'addresses',
            label: 'Contact Information',
            render: (addresses) => (
                <div className="text-sm">
                    <div className="text-gray-900 font-medium">{addresses?.[0]?.phone1 || 'No Phone'}</div>
                    <div className="text-gray-500 text-xs">{addresses?.[0]?.email1 || 'No Email'}</div>
                </div>
            )
        },
        {
            key: 'member_id',
            label: 'Internal ID',
            render: (val) => (
                <span className="font-mono text-xs text-gray-400">#{val}</span>
            )
        }
    ];

    const handleAddToGroup = async () => {
        setProcessing(true)
        try {
            const payload = {
                customer_id: targetCustomerId,
                group_id: targetGroupId,
            }
            const response = await client.postData('/users/add-customer-to-group', payload);
            // console.log(response);
            showToast(response.msg || 'Successfully added customer to group.', "success");
            setActiveModal(null);
            setTargetCustomerId('')
            fetchData();
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to add customer to group", "error");
        } finally {
            setProcessing(false)
        }
    };

    const handleBatchAction = async (actionType) => {
        setProcessing(true);
        try {
            // Placeholder for API calls
            const payload = {
                action_type: actionType,
                store_id: storeId,
                member_ids: selectedMemberIds,
                credit_limit: creditLimitAmount,
                current_group: groupId,
                target_group_id: targetGroupId,
            }
            let response;
            showToast(`Processing ${actionType} for ${selectedMemberIds.length} members`, "success");
            if (actionType === 'credit_limit') {
                // console.log(payload)
                response = await client.postData('/sales/batch-update-credit-line', payload);
            } else if (actionType === 'promote') {
                // console.log()
                response = await client.postData('/users/promote-customer-to-group',payload);
            }
            showToast(response?.msg, "info");
            setActiveModal(null);
            setSelectedMemberIds([]);
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.detail || "Action failed", "error");
        } finally {
            setProcessing(false);
        }
    };

    const handleRaiseInvoice = () => {
        if (!selectedMemberIds) {
            showToast('You must select group members to perform this action', "error");
            return;
        }
        navigate('/sales/invoices/new', { 
            state: { groupId:groupId, selectedMemberIds: selectedMemberIds }
        });
    };

    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="max-w-6xl mx-auto space-y-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <ArrowLeft size={18} /> Back to Groups
                </button>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-lg text-purple-600">
                            <Users size={28} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{groupData?.name || 'Loading group...'}</h1>
                            <p className="text-gray-500 text-sm">{groupData?.description}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="text-right">
                            <div className="text-xs text-gray-400 uppercase font-bold tracking-wider">Branch</div>
                            <div className="font-medium">{groupData?.stores?.[0]?.identifier}</div>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                    <DataTable1
                        loading={loading}
                        data={groupData?.members || []}
                        columns={columns}
                        enableSearch
                        rowsPerPage={15}
                    />

                    {/* Floating Mass Action Bar */}
{selectedMemberIds.length > 0 && (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-40 animate-in fade-in slide-in-from-bottom-4">
        <span className="text-sm font-medium border-r border-gray-700 pr-6">
            {selectedMemberIds.length} {selectedMemberIds.length === 1 ? 'member' : 'members'} selected
        </span>
        <div className="flex items-center gap-3">
            {/* Using native HTML buttons avoids internal variant text-color collisions completely */}
            <button
                type="button"
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 transition-all duration-200"
                onClick={() => setActiveModal('promote')}
            >
                <ArrowUpCircle size={18} className="text-green-400" /> Promote
            </button>
            
            <button
                type="button"
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 transition-all duration-200"
                onClick={() => setActiveModal('credit')}
            >
                <CreditCard size={18} className="text-blue-400" /> Credit Limit
            </button>
            
            <button
                type="button"
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 transition-all duration-200"
                onClick={handleRaiseInvoice}
            >
                <FileBadge2Icon size={18} className="text-blue-400" /> Raise Invoice
            </button>
            
            <button
                type="button"
                className="px-4 py-2 text-sm font-semibold text-red-400 rounded-lg hover:bg-red-950/40 flex items-center gap-2 transition-all duration-200"
            >
                <Trash2 size={18} /> Remove
            </button>
        </div>
        <button
            onClick={() => setSelectedMemberIds([])}
            className="ml-4 p-1 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors"
        >
            <X size={16} />
        </button>
    </div>
)}
                    {/* {selectedMemberIds.length > 0 && (
                        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-40 animate-in fade-in slide-in-from-bottom-4">
                            <span className="text-sm font-medium border-r border-gray-700 pr-6">
                                {selectedMemberIds.length} {selectedMemberIds.length === 1 ? 'member' : 'members'} selected
                            </span>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="ghost"
                                    className="text-white hover:bg-gray-800 hover:text-gray-700 flex items-center gap-2"
                                    onClick={() => setActiveModal('promote')}
                                >
                                    <ArrowUpCircle size={18} className="text-green-400" /> Promote
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="text-white hover:bg-gray-800 hover:text-gray-700 flex items-center gap-2"
                                    onClick={() => setActiveModal('credit')}
                                >
                                    <CreditCard size={18} className="text-blue-400" /> Credit Limit
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="text-white hover:bg-gray-800 hover:text-gray-700 flex items-center gap-2"
                                    onClick={handleRaiseInvoice}
                                >
                                    <FileBadge2Icon size={18} className="text-blue-400" /> Raise Invoice
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="text-white hover:bg-red-900/50 text-red-400 flex items-center gap-2"
                                >
                                    <Trash2 size={18} /> Remove
                                </Button>
                            </div>
                            <button
                                onClick={() => setSelectedMemberIds([])}
                                className="ml-4 p-1 hover:bg-gray-800 rounded-full"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    )} */}
                </div>
            </div>

            {/* --- Modals --- */}

            {/* Promote/Transfer Modal */}
            <Modal
                isOpen={activeModal === 'promote'}
                onClose={() => setActiveModal(null)}
                title="Promote Members to Group"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Transfer the {selectedMemberIds.length} selected members to a different group.
                    </p>
                    <SearchableSelectBox
                        label="Target Group"
                        placeholder="Search groups..."
                        options={groupOptions} // Fetch groups here
                        value={targetGroupId}
                        onChange={(e) => setTargetGroupId(e.target.value)}
                    />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" onClick={() => setActiveModal(null)}>Cancel</Button>
                        <Button
                            onClick={() => handleBatchAction('promote')}
                            loading={processing}
                        >
                            Confirm Transfer
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Bulk Credit Limit Modal */}
            <Modal
                isOpen={activeModal === 'credit'}
                onClose={() => setActiveModal(null)}
                title="Adjust Line of Credit"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Set a new credit limit for all {selectedMemberIds.length} selected members.
                    </p>
                    <Input
                        label="New Credit Limit"
                        type="number"
                        placeholder="0.00"
                        value={creditLimitAmount}
                        onChange={(e) => setCreditLimitAmount(e.target.value)}
                    />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" onClick={() => setActiveModal(null)}>Cancel</Button>
                        <Button
                            onClick={() => handleBatchAction('credit_limit')}
                            loading={processing}
                        >
                            Update Limits
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GroupMemberManager;